import { TDocumentDefinitions } from "pdfmake/interfaces";

import CompanyLogoForm from "react-lib/apps/HISV3/common/CompanyLogoForm";
import { meHealthDivision, OADetailType } from "react-lib/apps/HISV3/common/HeaderPdfFormTemplate";
import { formatISOtoBEDateTime } from "react-lib/utils/dateUtils";

export type FormSendSpecimenProps = {
  oaDetail?: OADetailType;
  startDate?: string;
  endDate?: string;
  reportSendSpecimen?: any[];
  printedBy?: string;
  printDate?: string;
  printTime?: string;
  division?: string;
  divisionCode?: string;
};

const COLOR = {
  black: "#000000",
  bannerBg: "#F9C1D0",
};

const FormSendSpecimen = async (props: FormSendSpecimenProps): Promise<TDocumentDefinitions> => {
  const { font, images, logoWidth } = await CompanyLogoForm({
    font: "THSarabunNew",
    form: "FormSendSpecimen",
    height: 50,
    logoImage: props.oaDetail?.imageFile,
  });

  const fields = props.reportSendSpecimen ?? [];

  const fileData: any[][] = [...fields]
    .sort(
      (a, b) =>
        new Date(a.collect_specimen_time).getTime() - new Date(b.collect_specimen_time).getTime(),
    )
    .map((item, index) => {
      const collectDateTime = formatISOtoBEDateTime(item.collect_specimen_time);
      const sendDateTime = formatISOtoBEDateTime(item.send_specimen_time);
      const divisionHn = (item?.patient_hn || "").split("_").pop()

      return [
        {
          text: index + 1,
          fontSize: 12,
          alignment: "center",
        },
        {
          text: `${collectDateTime?.date ?? ""} (${collectDateTime?.timeShort ?? ""})`,
          fontSize: 12,
          alignment: "center",
        },
        {
          text: `${divisionHn ?? ""} (${item.encounter_number ?? ""})`,
          fontSize: 12,
          alignment: "center",
        },
        {
          text: item.patient_name ?? "",
          fontSize: 12,
        },
        {
          text: item.specimen_name ?? "",
          fontSize: 12,
          alignment: "center",
        },
        {
          text: (item.test_names ?? []).flatMap((name: string, index: number) => [
            ...(index > 0 ? [{ text: "\n" }] : []),
            {
              text: `• ${name}`,
              fontSize: 12,
            },
          ]),
        },
        {
          text: item.vendor_name ?? "",
          fontSize: 12,
        },
        {
          text: sendDateTime?.date ? `${sendDateTime.date} (${sendDateTime.timeShort})` : "",
          fontSize: 12,
          alignment: "center",
        },
        {
          text: item.send_specimen_user_name ?? "",
          fontSize: 12,
          alignment: "center",
        },
      ];
    });

  return {
    defaultStyle: { font, lineHeight: 1.1 },
    images: { ...images },
    pageMargins: [20, 115, 20, 65],
    pageSize: "A4",
    pageOrientation: "landscape",
    header: () => ({
      margin: [20, 12, 20, 0],
      stack: [
        {
          columns: [
            props.oaDetail?.imageFile
              ? { image: "logo", width: logoWidth, height: 50, marginLeft: 15 }
              : { text: "", width: 55 },
            { text: "", width: 15 },
            {
              stack: meHealthDivision.includes(props.division || "")
                ? [
                    {
                      text: "มีเฮลท์ คลินิกเทคนิคการแพทย์",
                      bold: true,
                      fontSize: 16,
                      lineHeight: 1,
                    },
                    {
                      text: "261/27-28 ซอยจรัญสนิทวงศ์ 29/1 ถนนจรัญสนิทวงศ์ แขวงบางขุนศรี เขตบางกอกน้อย กรุงเทพฯ 10700",
                      fontSize: 12,
                      lineHeight: 1,
                    },
                    {
                      text: "โทร . 083-838-4657   อีเมล : mehealth@morishealth.com",
                      fontSize: 12,
                      lineHeight: 1,
                    },
                  ]
                : [
                    {
                      text: props.oaDetail?.name,
                      bold: true,
                      fontSize: 16,
                      lineHeight: 1,
                    },
                    {
                      text: props.oaDetail?.addressTH,
                      fontSize: 12,
                      lineHeight: 1,
                    },
                    {
                      text: "",
                      fontSize: 12,
                      lineHeight: 1,
                    },
                  ],
              width: "*",
              alignment: "left",
              marginLeft: 15,
            },
          ],
        },
        {
          table: {
            widths: ["*"],
            body: [
              [
                {
                  text: `รายงานบันทึกการส่งสิ่งส่งตรวจ วันที่ ${props.startDate} ถึง ${props.endDate}`,
                  bold: true,
                  fontSize: 18,
                  alignment: "center",
                  fillColor: COLOR.bannerBg,
                  margin: [0, 4, 0, 1],
                },
              ],
            ],
          },
          layout: { hLineWidth: () => 0, vLineWidth: () => 0 },
          marginTop: 10,
          marginBottom: 3,
        },
      ],
    }),
    content: [
      {
        stack: [
          {
            table: {
              headerRows: 1,
              widths: ["5%", "10%", "11%", "*", "10%", "15%", "12%", "10%", "10%"],
              body: [
                [
                  {
                    text: "ลำด้บ",
                    bold: true,
                    fontSize: 13,
                    alignment: "center",
                    marginTop: 10,
                  },
                  {
                    text: `วันที่เวลา\nเก็บสิ่งส่งตรวจ`,
                    bold: true,
                    fontSize: 13,
                    alignment: "center",
                  },
                  {
                    text: `MemberID\n(หมายเลขรับบริการ)`,
                    bold: true,
                    fontSize: 13,
                    alignment: "center",
                  },
                  {
                    text: "ชื่อผู้รับบริการ",
                    bold: true,
                    fontSize: 13,
                    alignment: "center",
                    marginTop: 10,
                  },
                  {
                    text: "ชนิดส่งสิ่งตรวจ",
                    bold: true,
                    fontSize: 13,
                    alignment: "center",
                    marginTop: 10,
                  },
                  {
                    text: "รายการตรวจ",
                    bold: true,
                    fontSize: 13,
                    alignment: "center",
                    marginTop: 10,
                  },
                  {
                    text: "Out Lab",
                    bold: true,
                    fontSize: 13,
                    alignment: "center",
                    marginTop: 10,
                  },
                  {
                    text: "วันที่เวลา\nส่งสิ่งส่งตรวจ",
                    bold: true,
                    fontSize: 13,
                    alignment: "center",
                  },
                  {
                    text: "เจ้าหน้าที่บันทึก\nส่งสิ่งส่งตรวจ",
                    bold: true,
                    fontSize: 13,
                    alignment: "center",
                  },
                ],
                ...fileData,
              ],
            },
          },
        ],
      },
    ],
    footer: function (currentPage: number, pageCount: number) {
      return {
        stack: [
          {
            width: "*",
            alignment: "left",
            text: [
              { text: "ผู้พิมพ์ (Printed by): ", fontSize: 12, bold: true },
              { text: `${props.printedBy}`, fontSize: 12 },
            ],
          },
          {
            columns: [
              {
                width: "*",
                alignment: "left",
                text: [
                  { text: "วัน เวลา ที่พิมพ์ (Printed Date/Time): ", fontSize: 12, bold: true },
                  { text: `${props.printDate} (${props.printTime})`, fontSize: 12 },
                ],
              },
              {
                width: "auto",
                alignment: "right",
                text: [
                  { text: "หน้าที่ (Page): ", fontSize: 12, bold: true },
                  { text: `${currentPage}/${pageCount}`, fontSize: 12 },
                ],
              },
            ],
          },
        ],
        margin: [20, 15, 20, 0],
      };
    },
  };
};

export default FormSendSpecimen;
